<template>

  <div>
    <Modal
      :title="title"
      :submit="submit"
      :submitting="submitting"
      submit-label="Apply"
    >

      <o-select
        label="Vertical"
        :items="VerticalRegistry.getOptions(['editInventory'])"
        v-model="v$.verticalType.$model"
        :validator="v$.verticalType"
        :readonly="!!verticalType"
      />

      <template v-if="formState.verticalType">

        <template v-if="locationProductsInSelectedVertical.length === 0">
          <p>No products in the "{{ VerticalRegistry.getTitle(formState.verticalType) }} " vertical are assigned to this location.</p>
        </template>

        <template v-if="locationProductsInSelectedVertical.length > 0">

          <!-- start and end date -->

          <v-row>
            <v-col>
              <o-text-field
                type="date"
                :label="formState.verticalType === 'event' ? 'Date' : 'Start Date'"
                v-model="v$.startDate.$model"
                :validator="v$.startDate"
              />
            </v-col>
            <v-col v-if="formState.verticalType !== 'event'">
              <o-text-field
                type="date"
                label="End Date"
                v-model="v$.endDate.$model"
                :validator="v$.endDate"
              />
            </v-col>
          </v-row>

          <!-- weekdays -->

          <div v-if="formState.verticalType !== 'event'" class="mb-8">

            <v-row>
              <v-col v-for="(weekdayName, weekdayIndex) in {1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 0: 'Sun'}">
                <v-checkbox
                  :label="weekdayName"
                  v-model="formState.weekdays"
                  :value="parseInt(weekdayIndex, 10)"
                  :disabled="!validWeekdays.has(parseInt(weekdayIndex, 10))"
                  hide-details
                />
              </v-col>
            </v-row>

            <div v-if="v$.weekdays.$error" class="text-error">
              <template v-for="$error in v$.weekdays.$errors">
                {{ $error.$message }}<br>
              </template>
            </div>

          </div>

          <!-- Location Products -->

          <template v-if="!dateRangeIsValid">
            <p class="text-center">Please select a date range to proceed</p>
          </template>

          <template v-if="dateRangeIsValid">

            <template v-if="formState.verticalType === 'event'">

              <AsyncPanel :status="eventsStatus">

                <p>Events</p>

                <v-alert
                  v-if="events.length === 0"
                  color="info"
                  text="No events found near this location on the specified date."
                />

                <div
                  v-if="events.length > 0"
                  style="overflow-y: scroll; max-height: 250px; margin-bottom: 30px"
                >

                  <v-table>
                    <tbody>
                    <tr v-for="event in sortedEvents" :key="event.ID">
                      <td>
                        <v-checkbox
                          type="checkbox"
                          :value="event.ID"
                          v-model="formState.eventIDs"
                          :id="'event_checkbox_' + event.ID"
                        />
                      </td>
                      <td>
                        <label :for="'event_checkbox_' + event.ID">
                          <strong>{{ event.name }}</strong>
                          <template v-if="event.venueName">
                            <br>
                            {{ event.venueName }}
                          </template>
                          <template v-if="event.eventStartLocal">
                            <br>
                            {{ moment.tz(event.eventStartLocal, location.timezone).format('MM/DD/YYYY h:mm a') }}
                          </template>
                        </label>
                      </td>
                      <td>
                        <template v-for="(channel, i) in event.channels">
                          <template v-if="i > 0">, </template>
                          {{ ChannelRegistry.find(channel)?.titleShort ?? channel }}
                        </template>
                      </td>
                    </tr>
                    </tbody>
                  </v-table>

                  <div v-if="v$.eventIDs.$error" class="text-error">
                    <template v-for="$error in v$.eventIDs.$errors">
                      {{ $error.$message }}<br>
                    </template>
                  </div>

                </div>

              </AsyncPanel>

            </template>

            <template v-if="showInventories">

              <template v-if="formState.inventories.length > 0">

                <ValidateEach
                  v-for="(inventory, index) in formState.inventories"
                  :key="(inventory.ID ? inventory.ID : 'null') + '_' + inventory.locationProductID + '_' + (inventory.eventID ? inventory.eventID : 'null')"
                  :state="inventory"
                  :rules="inventoryRules"
                >

                  <template #default="{ v }">
                    <o-text-field
                      :label="getInventoryFieldLabel(inventory)"
                      type="number"
                      :class="{'v-input--warning': v.spacesTotal.$model > location.onlineStalls && v.spacesTotal.$model <= location.totalStalls}"
                      v-model.number="v.spacesTotal.$model"
                      :validator="v.spacesTotal"
                      :hint="v.spacesTotal.$model > location.onlineStalls && v.spacesTotal.$model <= location.totalStalls ? 'Note: Exceeds the location\'s Total Allocation' : null"
                      persistent-hint
                    />
                  </template>

                </ValidateEach>

              </template>

              <v-alert
                v-if="invalidLocationProductsForSelectedDateRange.length > 0"
                color="warning"
              >
                <template v-slot:text>
                  <p>The following product(s) are inactive on at least one day in the selected date range. No inventory will be created for those days:</p>
                  <ul>
                    <li v-for="invalidLocationProduct in invalidLocationProductsForSelectedDateRange">
                      {{ invalidLocationProduct.product ? invalidLocationProduct.product.name : '(unknown)' }}
                    </li>
                  </ul>
                </template>
              </v-alert>

            </template>

          </template>

        </template>

      </template>

    </Modal>
  </div>

</template>

<script setup>

import { computed, ref, reactive, watch } from 'vue'

// components
import Modal from '../../../Modal.vue'
import AsyncPanel from '../../../AsyncPanel.vue'

// models
import Event from '../../../../models/Event'
import Location from '../../../../models/Location'
import LocationProduct from '../../../../models/LocationProduct'
import Product from '../../../../models/Product'
import { Inventory } from '../../../../models/Inventory'

// validation
import useVuelidate from '@vuelidate/core'
import { required, maxValue, minValue, integer } from '../../../../utils/i18n-validators'
import { helpers, requiredIf } from '@vuelidate/validators'
import { ValidateEach } from '@vuelidate/components'

// misc
import DateTimeUtils from '../../../../utils/DateTimeUtils'
import moment from 'moment'
import asyncStatus from '../../../../utils/asyncStatus'
import NotificationService from '../../../../services/NotificationService'
import { closeDialog } from 'vue3-promise-dialog'
import ChannelRegistry from '../../../../channels/ChannelRegistry'
import ProductTypeRegistry from '../../../../product_types/ProductTypeRegistry'
import VerticalRegistry from '../../../../verticals/VerticalRegistry'
import { Temporal } from '@js-temporal/polyfill'

const props = defineProps({

  locationID: {
    type: Number,
    required: true,
  },

  verticalType: {
    type: String,
  },

  startDate: {
    type: Temporal.PlainDate,
  },

  endDate: {
    type: Temporal.PlainDate,
  },

  eventID: {
    type: Number,
  },

})

/**
 * Returns a human-readable label for an Inventory or Rate item.
 *
 * If you're thinking that this function is kind of stupid, congratulations!
 * You are correct.
 *
 * @param locationProductID The item's locationProductID.
 * @param eventID The item's eventID (can be null).
 * @param events An array of all events that could possibly match the item.
 * @param timezone A string representing the timezone for the location in question
 */
function getInventoryOrRateLabel(locationProductID, eventID, events, timezone) {

  let label = ''
  let eventName = '(unknown event)'
  let productName = '(unknown product)'
  let productTypeName = '(unknown product type)'

  const locationProduct = LocationProduct.find(locationProductID)

  if (locationProduct) {
    const product = Product.find(locationProduct.productID)
    if (product) {
      productName = product.name
      productTypeName = ProductTypeRegistry.getTitle(product.productType)
    }
  }

  label = productTypeName

  if (productName.length) {
    label = productName + ' – ' + label
  }

  if (eventID) {
    const event = events.find(event => event.ID === eventID)
    if (event) {
      eventName = event.name
      if (event.date) {
        eventName += ' (' + moment.utc(event.date).tz(timezone).format('h:mm a') + ')'
      }
    }
    label = eventName + ' – ' + label

  }

  return label

}

const location = computed(() => {
  return Location.find(props.locationID)
})

// we assume that the parent component has already loaded LPs from the API
const locationProducts = computed(() => {
  return LocationProduct.query()
  .with(['product'])
  .where('locationID', props.locationID)
  .get()
})

const title = computed(() => {
  return (location.value ? location.value.locationName : '(unknown)') + ' – ' + VerticalRegistry.get(formState.verticalType)?.title ?? formState.verticalType
})

/**
 * Main form state and rules
 */

const formState = reactive({
  verticalType: props.verticalType,
  startDate: props.startDate ? props.startDate.toString() : '',
  endDate: props.endDate ? props.endDate.toString() : '',
  weekdays: [1, 2, 3, 4, 5, 6, 0],
  eventIDs: props.eventID ? [props.eventID] : [],
  inventories: [],
})

console.log(formState.eventIDs)

const isSameOrBeforeEndDate = (value) => {
  return !helpers.req(value) || !formState.endDate || moment(value).isSameOrBefore(formState.endDate)
}

const hasValidYear = (value) => {
  const momentValue = moment(value)
  return !helpers.req(value) || momentValue.isValid() && momentValue.year() >= 2020
}

const mainFormRules = {
  verticalType: { required },
  startDate: {
    required,
    isSameOrBeforeEndDate: helpers.withMessage(
      'Must be before End Date',
      isSameOrBeforeEndDate
    ),
    hasValidYear: helpers.withMessage(
      'Year must be 2020 or later',
      hasValidYear
    ),
  },
  endDate: {
    required,
    hasValidYear: helpers.withMessage(
      'Year must be 2020 or later',
      hasValidYear
    ),
  },
  weekdays: {
    required: helpers.withMessage(
      'You must select at least one weekday',
      requiredIf(() => {
        return formState.verticalType && formState.verticalType !== 'event'
      })
    ),
  },
  eventIDs: {
    isNonEmptyIfVerticalIsEvent: helpers.withMessage(
      'Please select at least one event',
      (value) => {
        if (formState.verticalType === 'event') {
          return value.length > 0
        }
        return true
      }
    ),
  },
}

const v$ = useVuelidate(mainFormRules, formState)

// force Vuelidate to consider this validator dirty, so that errors will be populated into v$.$errors instead of v$.$silentErrors
v$.value.eventIDs.$touch()

const dateRangeIsValid = computed(() => {
  return !v$.value.startDate.$invalid && !v$.value.endDate.$invalid
})

const locationProductsInSelectedVertical = computed(() => {

  return locationProducts.value.filter((locationProduct) => {
    return locationProduct.product && (locationProduct.product.verticalType === formState.verticalType)
  })

})

const validWeekdays = computed(() => {

  if (formState.verticalType === 'event') {
    return new Set([1, 2, 3, 4, 5, 6, 0])
  }

  if (!dateRangeIsValid.value) {
    return new Set()
  }

  const weekdaysInDateRange = DateTimeUtils.getWeekdaysInDateRange(
    formState.startDate,
    formState.endDate
  )

  return new Set(weekdaysInDateRange)

})

/**
 * Transient and Monthly products are restricted by weekday.
 * Return a list of LocationProducts (in the selected vertical) that are
 * inactive on at least one day in the selected date range.
 */
const invalidLocationProductsForSelectedDateRange = computed(() => {

  if (formState.verticalType !== 'transient' && formState.verticalType !== 'monthly') {
    // We aren't in a vertical that can be constrained by weekday
    return []
  }

  if (!dateRangeIsValid.value) {
    return []
  }

  const weekdays = DateTimeUtils.getWeekdaysInDateRange(
    formState.startDate,
    formState.endDate
  )

  return locationProductsInSelectedVertical.value.filter((locationProduct) => {

    if (!locationProduct.product) {
      return false
    }

    const weekdayProperties = [
      'daysSun',
      'daysMon',
      'daysTue',
      'daysWed',
      'daysThu',
      'daysFri',
      'daysSat',
    ]

    // Iterate over the weekdays that are part of the selected date range.
    // If the product is disabled on any of them, then count this as an invalid
    // LocationProduct.
    for (let i = 0; i < weekdays.length; i++) {
      const weekday = weekdays[i]
      const weekdayProperty = weekdayProperties[weekday]
      if (!locationProduct.product[weekdayProperty]) {
        return true
      }
    }

    // This LocationProduct is valid for all days in the selected date range.
    return false

  })

})

// populate formState.inventories at init time...
updateFormInventories()

// ...and whenever dependent data changes
watch(
  [
    () => formState.eventIDs,
    locationProductsInSelectedVertical,
  ],
  () => {
    updateFormInventories()
  }
)

function updateFormInventories() {

  const desiredInventories = []

  // build a list of the locationProductID / eventID combinations that *should*
  // exist in formState.inventories

  if (formState.verticalType === 'event') {
    formState.eventIDs.forEach((eventID) => {
      locationProductsInSelectedVertical.value.forEach((locationProduct) => {
        desiredInventories.push({
          locationProductID: locationProduct.ID,
          eventID: eventID,
        })
      })
    })
  } else {
    locationProductsInSelectedVertical.value.forEach((locationProduct) => {
      desiredInventories.push({
        locationProductID: locationProduct.ID,
        eventID: null,
      })
    })
  }

  // now synchronize formState.inventories with this list

  // Insert any items that aren't already in formState.inventories
  desiredInventories.forEach((desiredInventory) => {
    const existingInventory = formState.inventories.find((formInventory) => {
      return formInventory.locationProductID === desiredInventory.locationProductID && formInventory.eventID === desiredInventory.eventID
    })
    if (!existingInventory) {
      formState.inventories.push({
        ID: null,
        locationProductID: desiredInventory.locationProductID,
        eventID: desiredInventory.eventID,
        spacesTotal: null,
      })
    }
  })

  // Remove items that are in formState.inventories but not in desiredInventories.
  // We do this backwards so that removing items doesn't mess up the .forEach()
  // loop.
  for (let index = formState.inventories.length - 1; index >=0; index--) {
    let formInventory = formState.inventories[index]
    const desiredInventory = desiredInventories.find((desiredInventory) => {
      return desiredInventory.locationProductID === formInventory.locationProductID && desiredInventory.eventID === formInventory.eventID
    })
    if (!desiredInventory) {
      formState.inventories.splice(index, 1)
    }
  }

}

const inventoryRules = {
  spacesTotal: {
    integer,
    minValue: minValue(0),
    maxValue: helpers.withMessage(
      'Cannot exceed the location\'s Total Parking Assets Stalls',
      maxValue(location.value.totalStalls)
    ),
  },
}

/**
 * Events
 */

const events = ref([])

const sortedEvents = computed(() => {
  return events.value.sort((event1, event2) => {
    if (event1.eventStartLocal > event2.eventStartLocal) {
      return 1
    } else if (event2.eventStartLocal > event1.eventStartLocal) {
      return -1
    }
    return 0
  })
})

const eventsStatus = asyncStatus()

// When startDate is changed, clear any selected eventIDs, and load events for the new date range

watch(
  [
    () => formState.verticalType,
    () => formState.startDate,
  ],
  () => {
    formState.eventIDs.length = 0
    if (formState.verticalType === 'event') {
      formState.endDate = formState.startDate
      loadEvents()
    }
  }
)

// also load 'em at init time
loadEvents()

// Load events within the specified date range
function loadEvents() {

  if (formState.verticalType !== 'event') {
    return
  }

  if (!dateRangeIsValid.value) {
    events.value = []
    return
  }

  eventsStatus.setBusy()

  Event
  .fetchByLocationID(props.locationID, {
    start: moment(formState.startDate).format(moment.HTML5_FMT.DATE),
    end: moment(formState.endDate).format(moment.HTML5_FMT.DATE),
  })
  .then((result) => {
    events.value = result.response.data.events
    eventsStatus.setReady()
  })
  .catch((error) => {
    eventsStatus.setError(error)
  })

}

/**
 * If verticalType is 'event', then don't show the list of inventory fields
 * until the user has checked at least one event.
 */
const showInventories = computed(() => {
  const show =
    formState.verticalType !== 'event' ||
    (
      formState.verticalType === 'event' &&
      formState.eventIDs.length > 0
    )
  return show
})

/**
 * Form submission
 */

const submitting = ref(false)

async function submit() {

  // Uncheck invalid weekdays (simply disabling the checkbox doesn't do this)
  formState.weekdays = formState.weekdays.filter((weekday) => {
    return validWeekdays.value.has(weekday)
  })

  // force validation to run
  const isValid = await v$.value.$validate()

  if (!isValid) {
    return
  }

  submitting.value = true

  const payload = {
    startDate: moment(formState.startDate).format(moment.HTML5_FMT.DATE),
    endDate: moment(formState.endDate).format(moment.HTML5_FMT.DATE),
    weekdays: formState.weekdays,
    inventories: [],
  }

  let weekdays = []
  let checkWeekdays = false

  if (formState.verticalType === 'transient' || formState.verticalType === 'monthly') {
    weekdays = DateTimeUtils.getWeekdaysInDateRange(
      formState.startDate,
      formState.endDate
    )
    checkWeekdays = true
  }

  formState.inventories.forEach((formInventory) => {

    // if this field is empty, don't create inventory
    if (formInventory.spacesTotal === null || formInventory.spacesTotal === '') {
      return
    }

    // if this LP isn't enabled on any days in the selected date range, don't
    // try to create inventory (the API will complain)
    if (checkWeekdays && !lpIsActiveOnAtLeastOneWeekday(formInventory.locationProductID, weekdays)) {
      return
    }

    payload.inventories.push({
      locationProductID: formInventory.locationProductID,
      eventID: formInventory.eventID,
      spacesTotal: formInventory.spacesTotal,
    })

  })

  // If we're not actually updating any inventory, then don't bother making the
  // API call
  if (payload.inventories.length === 0) {
    submitting.value = false
    closeDialog(false)
    return
  }

  Inventory.updateByLocationID(props.locationID, payload)
  .then(() => {
    NotificationService.success('Inventory updated')
    closeDialog(true)
  })
  .catch((error) => {
    console.error(error)
    NotificationService.error('Couldn\'t update inventory!')
  })
  .finally(() => {
    submitting.value = false
  })

}

/**
 * Utility functions
 */

// TODO: this probably won't update when dependent data changes. Might need/want to move the fields into their own component, so this can be reactive
function getInventoryFieldLabel(inventory) {
  return getInventoryOrRateLabel(
    inventory.locationProductID,
    inventory.eventID,
    events.value,
    location.value.timezone
  )
}

function lpIsActiveOnAtLeastOneWeekday(locationProductID, weekdays) {

  const locationProduct = locationProducts.value.find(locationProduct => locationProduct.ID === locationProductID)

  const weekdayProperties = [
    'daysSun',
    'daysMon',
    'daysTue',
    'daysWed',
    'daysThu',
    'daysFri',
    'daysSat',
  ]

  let weekday, weekdayProperty

  for (let i = 0; i < weekdays.length; i++) {
    weekday = weekdays[i]
    weekdayProperty = weekdayProperties[weekday]
    if (locationProduct.product[weekdayProperty]) {
      return true
    }
  }

  return false

}

</script>

<style lang="scss">

.v-input--warning {

  .v-label.v-field-label,
  .v-field__outline,
  .v-messages {
    color: rgb(var(--v-theme-warning));
  }

}

</style>
